<template>
  <Teleport to="body">
    <Modal v-model="showModal">
      <ModalContent
        size="full"
        persistent
        class="tw-flex tw-h-[calc(100vh-2rem)] tw-flex-col tw-overflow-x-hidden [&>div]:tw-flex-1 [&>div]:tw-overflow-hidden [&>div]:tw-p-0"
      >
        <template #header>
          <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-6">
            <div>
              <Heading level="h2" level-class="h3">{{ $t('notifications_modal.title') }}</Heading>
              <Text v-if="totalCount > 0" class="tw-mt-0.5 tw-text-sm tw-opacity-60">
                {{ $t('notifications_modal.count', { new: newMessages.length, total: totalCount }) }}
              </Text>
            </div>

            <CircularButton
              icon="close"
              color="transparentWhite"
              :aria-label="$t('close')"
              @click="store.closeModal()"
            />
          </div>
        </template>

        <div class="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-px-6 tw-pt-6">
          <div
            v-if="totalCount > 0"
            class="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-white/10 tw-pb-4"
          >
            <div class="tw-flex tw-items-center tw-gap-3">
              <Text class="tw-text-sm tw-opacity-60">{{ $t('notifications_modal.severity_label') }}</Text>
              <div class="tw-flex tw-gap-2">
                <Tag
                  v-for="level in SEVERITY_LEVELS"
                  :key="level"
                  size="small"
                  color="dark"
                  :class="[
                    'tw-cursor-pointer tw-transition-opacity',
                    { 'tw-opacity-30': !activeSeverityFilters.has(level) },
                    activeSeverityFilters.has(level) ? severityActiveClass(level) : '',
                  ]"
                  @click="toggleSeverityFilter(level)"
                >
                  {{ $t(`notifications_modal.severity.${level}`) }}
                </Tag>
              </div>
            </div>
            <Checkbox
              v-model="groupByStation"
              type="switch"
              :right-label="$t('notifications_modal.group_by_station')"
            />
          </div>

          <div
            v-if="totalCount === 0"
            class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-opacity-30"
          >
            <Icon name="bell" class="tw-size-16 tw-fill-current" />
            <Text size="xlarge">{{ $t('notifications_modal.empty') }}</Text>
          </div>

          <div v-else class="tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-pb-4">
            <MyDetails
              v-for="section in sections"
              :key="section.key"
              class="tw-group"
              :open="section.open || undefined"
              :summary-attrs="{
                color: 'transparentWhite',
                class: `tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-3${section.borderBottom ? ' tw-border-b tw-border-white/10' : ''}`,
              }"
            >
              <template #summary>
                <div class="tw-flex tw-items-center tw-gap-2">
                  <Icon
                    name="chevron-down"
                    class="tw-size-4 -tw-rotate-90 tw-fill-current tw-transition-transform group-open:tw-rotate-0"
                  />
                  <Text class="tw-text-sm tw-font-bold tw-uppercase tw-tracking-wider">
                    {{ $t(`notifications_modal.sections.${section.key}`) }}
                  </Text>
                  <span class="tw-rounded-full tw-bg-white/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-bold">
                    {{ section.messages.length }}
                  </span>
                </div>
                <Button v-if="section.action" color="transparentWhite" size="small" @click.stop="section.action.fn()">
                  {{ section.action.label }}
                </Button>
              </template>

              <template #body>
                <template v-if="section.messages.length > 0">
                  <div class="tw-flex tw-flex-col tw-gap-2 tw-p-2 sm:tw-hidden">
                    <template v-for="(row, rowIdx) in section.displayRows" :key="rowIdx">
                      <div
                        v-if="row.type === 'station_header'"
                        class="tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-px-1 tw-pt-2"
                        @click="toggleStationCollapse(row.label)"
                      >
                        <Icon
                          name="chevron-down"
                          :class="[
                            'tw-size-4 tw-fill-current tw-transition-transform',
                            collapsedStations.has(row.label) ? '-tw-rotate-90' : 'tw-rotate-0',
                          ]"
                        />
                        <Text class="tw-font-semibold tw-opacity-70">{{ row.label }}</Text>
                        <span
                          :class="['tw-rounded-full tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-bold tw-text-white', levelBgClass(row.highestLevel)]"
                        >{{ row.count }}</span>
                      </div>

                      <NotificationCard
                        v-else-if="row.type === 'msg'"
                        :msg="row.msg"
                        :section-key="section.key"
                        :muted="isMuted(row.msg.id)"
                        :bell-icon="section.getBellIcon ? section.getBellIcon(row.msg) : null"
                        :expanded-detail="expandedDetailRows.has(row.msg.id)"
                        :class="section.getMsgOpacity(row.msg)"
                        @toggle-mute="section.getBellAction && section.getBellAction(row.msg)"
                        @toggle-detail="toggleExpandDetail(row.msg.id)"
                      />
                    </template>
                  </div>

                  <Box class="tw-hidden sm:tw-block">
                    <Table class="tw-min-w-full">
                    <TableHeader>
                      <TableRow>
                        <TableCell class="tw-w-10 tw-p-1" />
                        <TableCell class="tw-w-36">{{ $t('notifications_modal.columns.created_time') }}</TableCell>
                        <TableCell class="tw-w-24">{{ $t('notifications_modal.columns.duration') }}</TableCell>
                        <TableCell>{{ $t('notifications_modal.columns.station') }}</TableCell>
                        <TableCell>{{ $t('notifications_modal.columns.title') }}</TableCell>
                        <TableCell>{{ $t('notifications_modal.columns.message') }}</TableCell>
                        <TableCell class="tw-w-36" />
                      </TableRow>
                    </TableHeader>

                    <template
                      v-for="(row, rowIdx) in section.displayRows"
                      :key="
                        row.type === 'msg'
                          ? row.msg.id
                          : row.type === 'station_header'
                            ? `header-${row.label}`
                            : `spacer-${rowIdx}`
                      "
                    >
                      <tbody v-if="row.type === 'group_spacer'">
                        <tr class="tw-h-2" aria-hidden="true" />
                      </tbody>

                      <tbody v-else-if="row.type === 'station_header'">
                        <tr
                          class="tw-cursor-pointer tw-select-none hover:tw-bg-white/5"
                          @click="toggleStationCollapse(row.label)"
                        >
                          <td :colspan="columnCount" class="tw-px-3 tw-py-2">
                            <div class="tw-flex tw-items-center tw-gap-2">
                              <Icon
                                name="chevron-down"
                                :class="[
                                  'tw-size-4 tw-fill-current tw-transition-transform',
                                  collapsedStations.has(row.label) ? '-tw-rotate-90' : 'tw-rotate-0',
                                ]"
                              />
                              <Text class="tw-font-semibold tw-opacity-70">{{ row.label }}</Text>
                              <span class="tw-relative tw-inline-flex tw-items-center">
                                <span
                                  v-if="section.key === 'active'"
                                  :class="[
                                    'tw-absolute tw-inline-flex tw-size-full tw-animate-ping tw-rounded-full tw-opacity-60',
                                    levelBgClass(row.highestLevel),
                                  ]"
                                />
                                <span
                                  :class="[
                                    'tw-relative tw-rounded-full tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-bold tw-text-white',
                                    levelBgClass(row.highestLevel),
                                  ]"
                                  >{{ row.count }}</span
                                >
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tbody>

                      <tbody
                        v-else-if="row.type === 'msg'"
                        :class="section.getMsgOpacity(row.msg)"
                        @mouseenter="handleRowMouseEnter(row.msg)"
                        @mouseleave="hoveredMessageId = null"
                      >
                        <tr v-if="!row.isFirst" class="tw-h-0.5" aria-hidden="true" />
                        <TableRow with-border class="tw-align-middle" :class="levelRowBgClass(row.msg.level)">
                          <TableCell class="tw-w-10 tw-border-l-2 tw-p-1" :class="[hoveredClass(row.msg.id), levelBorderClass(row.msg.level)]">
                            <CircularButton
                              v-if="section.getBellAction"
                              :icon="section.getBellIcon(row.msg)"
                              color="transparentWhite"
                              shape="rounded"
                              size="small"
                              :class="levelTextClass(row.msg.level)"
                              :aria-label="isMuted(row.msg.id) ? $t('notifications_modal.actions.unmute') : $t('notifications_modal.actions.mute')"
                              @click="section.getBellAction(row.msg)"
                            />
                          </TableCell>
                          <TableCell
                            class="tw-w-24 tw-whitespace-nowrap tw-text-sm tw-tabular-nums"
                            :class="hoveredClass(row.msg.id)"
                          >
                            {{ formatLocalTime(row.msg.created_at) }}
                          </TableCell>
                          <TableCell
                            class="tw-w-24 tw-whitespace-nowrap tw-text-sm tw-tabular-nums"
                            :class="hoveredClass(row.msg.id)"
                          >
                            {{ formatDuration(row.msg.duration) }}
                          </TableCell>
                          <TableCell class="tw-max-w-36 tw-truncate tw-text-sm" :class="hoveredClass(row.msg.id)">
                            <Link
                              v-if="stationUrlMap.get(row.msg.station_label)"
                              :href="stationUrlMap.get(row.msg.station_label)"
                              scheme="underline"
                              target="_blank"
                            >{{ row.msg.station_label }}</Link>
                            <template v-else>{{ row.msg.station_label }}</template>
                          </TableCell>
                          <TableCell
                            class="tw-text-sm tw-font-bold"
                            :class="[hoveredClass(row.msg.id), levelTextClass(row.msg.level)]"
                            >{{ row.msg.title }}</TableCell
                          >
                          <TableCell class="tw-max-w-xs" :class="hoveredClass(row.msg.id)">
                            <span
                              :ref="
                                (el) => {
                                  el ? textRefs.set(row.msg.id, el) : textRefs.delete(row.msg.id);
                                }
                              "
                              :class="['tw-block tw-text-sm', !expandedTextIds.has(row.msg.id) && 'tw-line-clamp-3']"
                              >{{ row.msg.text }}</span
                            >
                            <Button
                              v-if="overflowingTextIds.has(row.msg.id) || expandedTextIds.has(row.msg.id)"
                              color="transparent"
                              size="small"
                              class="tw-mt-1"
                              @click="toggleExpandText(row.msg.id)"
                            >
                              {{ expandedTextIds.has(row.msg.id) ? '▲' : '▶' }}
                            </Button>
                          </TableCell>
                          <TableCell class="tw-w-36" :class="hoveredClass(row.msg.id)">
                            <Button
                              v-if="row.msg.details"
                              color="semiTransparentWhite"
                              size="small"
                              @click="toggleExpandDetail(row.msg.id)"
                            >
                              <span class="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap">
                                <Icon
                                  name="chevron-down"
                                  :class="[
                                    'tw-size-4 tw-fill-current tw-transition-transform',
                                    expandedDetailRows.has(row.msg.id) ? 'tw-rotate-0' : '-tw-rotate-90',
                                  ]"
                                />
                                {{
                                  expandedDetailRows.has(row.msg.id)
                                    ? $t('notifications_modal.actions.hide_details')
                                    : $t('notifications_modal.actions.show_details')
                                }}
                              </span>
                            </Button>
                          </TableCell>
                        </TableRow>

                        <tr
                          v-if="row.msg.details && expandedDetailRows.has(row.msg.id)"
                          :class="levelRowBgClass(row.msg.level)"
                        >
                          <td
                            :colspan="columnCount"
                            class="tw-border-l-2 tw-p-3"
                            :class="[hoveredClass(row.msg.id), levelBorderClass(row.msg.level)]"
                          >
                            <pre class="tw-whitespace-pre-wrap tw-rounded tw-bg-white/10 tw-p-3 tw-text-sm">{{
                              row.msg.details
                            }}</pre>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                    </Table>
                  </Box>
                </template>

                <Text
                  v-else-if="section.key === 'active' && section.allMessages.length > 0"
                  class="tw-block tw-px-4 tw-py-3 tw-text-sm tw-opacity-50"
                >
                  {{ $t('notifications_modal.all_filtered') }}
                </Text>
                <Text v-else class="tw-block tw-px-4 tw-py-3 tw-text-sm tw-opacity-40">
                  {{ $t(`notifications_modal.sections.${section.key}_empty`) }}
                </Text>
              </template>
            </MyDetails>
          </div>
        </div>

        <template #footer="{ reject }">
          <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4">
            <div class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-opacity-50">
              <span class="tw-relative tw-inline-flex tw-size-2 tw-shrink-0">
                <span
                  class="tw-absolute tw-inline-flex tw-size-full tw-animate-ping tw-rounded-full tw-bg-state-succ tw-opacity-75"
                />
                <span class="tw-relative tw-inline-flex tw-size-2 tw-rounded-full tw-bg-state-succ" />
              </span>
              <span>{{ $t('notifications_modal.live_from_backend') }}</span>
              <span class="tw-opacity-50">·</span>
              <span>{{ $t('notifications_modal.footer_note') }}</span>
            </div>
            <Button color="transparent" outlined @click="reject">{{ $t('close') }}</Button>
          </div>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  Box,
  Button,
  Checkbox,
  CircularButton,
  Heading,
  Icon,
  Link,
  Modal,
  ModalContent,
  MyDetails,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  Tag,
  Text,
} from '@slideslive/fuse-kit/vue';

import BellOffOutlineSvg from '@/assets/mdi/bell-off-outline.svg';
import BellOutlineSvg from '@/assets/mdi/bell-outline.svg';
import BellRingOutlineSvg from '@/assets/mdi/bell-ring-outline.svg';
import { formatDuration, formatLocalTime } from '@/composables/state_notifications';
import useNotificationsModalStore from '@/stores/notifications_modal';
import useStationsStore from '@/stores/stations';

import NotificationCard from './notificationsModal/NotificationCard.vue';

const { t } = useI18n();
const store = useNotificationsModalStore();

const bellRing = ref(false);
let bellTimer = null;
onMounted(() => {
  bellTimer = setInterval(() => {
    bellRing.value = !bellRing.value;
  }, 700);
});
onUnmounted(() => clearInterval(bellTimer));
const { newMessages, mutedMessages, inactiveMessages, mutedMessageIds, focusStationLabel } = storeToRefs(store);

const isMuted = (id) => mutedMessageIds.value.has(id);

const stationsStore = useStationsStore();
const { selectedStationsList } = storeToRefs(stationsStore);

const groupByStation = ref(true);

const stationOrderMap = computed(() => {
  const map = new Map();
  selectedStationsList.value.forEach((station, index) => {
    const label = [station.stationName, station.trackName].filter(Boolean).join(': ');
    map.set(label, index);
  });
  return map;
});

const stationUrlMap = computed(() => {
  const map = new Map();
  for (const station of selectedStationsList.value) {
    const label = [station.stationName, station.trackName].filter(Boolean).join(': ');
    const url = station.nateRunnerState?.data?.nate?.ui_url ?? null;
    if (url) map.set(label, url);
  }
  return map;
});

const showModal = computed({
  get: () => store.show,
  set: (v) => (v ? store.openModal() : store.closeModal()),
});

const totalCount = computed(
  () => newMessages.value.length + mutedMessages.value.length + inactiveMessages.value.length,
);

const SEVERITY_LEVELS = ['info', 'warning', 'error'];
const activeSeverityFilters = ref(new Set(['error', 'warning', 'info']));

const toggleSeverityFilter = (level) => {
  const next = new Set(activeSeverityFilters.value);
  next.has(level) ? next.delete(level) : next.add(level);
  activeSeverityFilters.value = next;
};

const columnCount = 7;

const filterMessages = (msgs) => msgs.filter((m) => activeSeverityFilters.value.has(m.level));

const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };

const filteredActive = computed(() => {
  const msgs = filterMessages([...newMessages.value, ...mutedMessages.value]);
  return [...msgs].sort((a, b) => {
    const aMuted = isMuted(a.id) ? 1 : 0;
    const bMuted = isMuted(b.id) ? 1 : 0;
    if (aMuted !== bMuted) return aMuted - bMuted;
    const sev = (SEVERITY_ORDER[a.level] ?? 3) - (SEVERITY_ORDER[b.level] ?? 3);
    if (sev !== 0) return sev;
    return new Date(b.created_at) - new Date(a.created_at);
  });
});
const filteredInactive = computed(() =>
  [...filterMessages(inactiveMessages.value)].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
);

const getBellIcon = (msg) => {
  if (isMuted(msg.id)) return BellOffOutlineSvg;
  return bellRing.value ? BellRingOutlineSvg : BellOutlineSvg;
};

const toggleMute = (msg) => {
  isMuted(msg.id) ? store.unmuteMessage(msg.id) : store.muteMessage(msg.id);
};

const SPAM_THRESHOLD = 10;
const collapsedStations = ref(new Set());

watch(focusStationLabel, (label) => {
  if (!label) return;
  const allLabels = new Set([
    ...newMessages.value.map((m) => m.station_label),
    ...mutedMessages.value.map((m) => m.station_label),
    ...inactiveMessages.value.map((m) => m.station_label),
  ]);
  allLabels.delete(label);
  collapsedStations.value = allLabels;
  groupByStation.value = true;
});

const spamStationLabels = computed(() => {
  const counts = new Map();
  for (const msg of [...newMessages.value, ...mutedMessages.value, ...inactiveMessages.value]) {
    counts.set(msg.station_label, (counts.get(msg.station_label) ?? 0) + 1);
  }
  return new Set([...counts.entries()].filter(([, c]) => c > SPAM_THRESHOLD).map(([l]) => l));
});

watch(spamStationLabels, (newSpam, oldSpam) => {
  const newlySpamming = [...newSpam].filter((l) => !oldSpam?.has(l));
  if (!newlySpamming.length) return;
  const next = new Set(collapsedStations.value);
  for (const label of newlySpamming) next.add(label);
  collapsedStations.value = next;
});

watch(groupByStation, (enabled) => {
  if (!enabled) {
    collapsedStations.value = new Set();
    return;
  }
  collapsedStations.value = new Set(spamStationLabels.value);
});

const toggleStationCollapse = (label) => {
  const next = new Set(collapsedStations.value);
  next.has(label) ? next.delete(label) : next.add(label);
  collapsedStations.value = next;
};

const sortByTime = (arr) => [...arr].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

const highestLevel = (msgs) => {
  if (msgs.some((m) => m.level === 'error')) return 'error';
  if (msgs.some((m) => m.level === 'warning')) return 'warning';
  return 'info';
};

const levelBgClass = (level) =>
  ({ error: 'tw-bg-state-err', warning: 'tw-bg-state-warn', info: 'tw-bg-state-info' })[level] ?? 'tw-bg-white/20';

const groupMessages = (msgs) => {
  const groups = new Map();
  for (const msg of msgs) {
    const label = msg.station_label || '';
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(msg);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => {
      const aOrder = stationOrderMap.value.get(a) ?? Infinity;
      const bOrder = stationOrderMap.value.get(b) ?? Infinity;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.localeCompare(b);
    })
    .map(([label, messages]) => ({ label, messages: sortByTime(messages) }));
};

const pushGroupHeader = (rows, label, messages) => {
  if (rows.length > 0) rows.push({ type: 'group_spacer' });
  rows.push({ type: 'station_header', label, count: messages.length, highestLevel: highestLevel(messages) });
};

const buildDisplayRows = (msgs) => {
  if (groupByStation.value) {
    const rows = [];
    for (const group of groupMessages(msgs)) {
      pushGroupHeader(rows, group.label, group.messages);
      if (!collapsedStations.value.has(group.label)) {
        group.messages.forEach((msg, i) => rows.push({ type: 'msg', msg, isFirst: i === 0 }));
      }
    }
    return rows;
  }

  const spamMsgs = new Map();
  const flatMsgs = [];
  for (const msg of msgs) {
    if (spamStationLabels.value.has(msg.station_label)) {
      if (!spamMsgs.has(msg.station_label)) spamMsgs.set(msg.station_label, []);
      spamMsgs.get(msg.station_label).push(msg);
    } else {
      flatMsgs.push(msg);
    }
  }
  const rows = [];
  flatMsgs.forEach((msg, i) => rows.push({ type: 'msg', msg, isFirst: i === 0 }));
  for (const [label, groupMsgs] of spamMsgs) {
    pushGroupHeader(rows, label, groupMsgs);
    if (!collapsedStations.value.has(label)) {
      sortByTime(groupMsgs).forEach((msg, i) => rows.push({ type: 'msg', msg, isFirst: i === 0 }));
    }
  }
  return rows;
};

const expandedDetailRows = ref(new Set());

const toggleExpandDetail = (id) => {
  const next = new Set(expandedDetailRows.value);
  next.has(id) ? next.delete(id) : next.add(id);
  expandedDetailRows.value = next;
};

const expandedTextIds = ref(new Set());

const toggleExpandText = (id) => {
  const next = new Set(expandedTextIds.value);
  next.has(id) ? next.delete(id) : next.add(id);
  expandedTextIds.value = next;
};

const textRefs = new Map();
const overflowingTextIds = ref(new Set());

const checkTextOverflow = () => {
  const next = new Set();
  for (const [id, el] of textRefs) {
    if (el && el.scrollHeight > el.clientHeight) next.add(id);
  }
  const prev = overflowingTextIds.value;
  if (next.size !== prev.size || [...next].some((id) => !prev.has(id))) {
    overflowingTextIds.value = next;
  }
};

onMounted(checkTextOverflow);

watch([filteredActive, filteredInactive, expandedTextIds, collapsedStations], checkTextOverflow, {
  flush: 'post',
});

const hoveredMessageId = ref(null);

const handleRowMouseEnter = (msg) => {
  if (msg.details && expandedDetailRows.value.has(msg.id)) {
    hoveredMessageId.value = msg.id;
  }
};

const hoveredClass = (id) => ({ 'fuse-bg-purple/15': hoveredMessageId.value === id });

const sections = computed(() => [
  {
    key: 'active',
    messages: filteredActive.value,
    displayRows: buildDisplayRows(filteredActive.value),
    allMessages: [...newMessages.value, ...mutedMessages.value],
    open: true,
    borderBottom: true,
    getBellIcon,
    getBellAction: toggleMute,
    getMsgOpacity: (msg) => (isMuted(msg.id) ? 'tw-opacity-50' : null),
    action:
      newMessages.value.length > 0 ? { label: t('notifications_modal.mute_all'), fn: () => store.muteAll() } : null,
  },
  {
    key: 'inactive',
    messages: filteredInactive.value,
    displayRows: buildDisplayRows(filteredInactive.value),
    allMessages: inactiveMessages.value,
    open: true,
    borderBottom: false,
    getBellIcon: null,
    getBellAction: null,
    getMsgOpacity: () => 'tw-opacity-40',
    action:
      inactiveMessages.value.length > 0
        ? { label: t('notifications_modal.clear_all'), fn: () => store.clearInactive() }
        : null,
  },
]);

const levelTextClass = (level) =>
  ({ error: 'tw-text-state-err', warning: 'tw-text-state-warn', info: 'tw-text-state-info' })[level] ?? '';

const levelBorderClass = (level) =>
  ({ error: 'tw-border-state-err', warning: 'tw-border-state-warn', info: 'tw-border-state-info' })[level] ?? 'tw-border-white/20';

const levelRowBgClass = (level) =>
  ({ error: 'tw-bg-state-err/10', warning: 'tw-bg-state-warn/10', info: 'tw-bg-state-info/10' })[level] ?? '';

const severityActiveClass = (level) =>
  ({ error: 'tw-bg-state-err', warning: 'tw-bg-state-warn', info: 'tw-bg-state-info' })[level] ?? '';
</script>
